// Reads the payload Vriti's host page leaves in
// `sessionStorage['ai-interview-schedule-data']` when it opens the scheduler.
//
// Three shapes exist in the wild and ALL are accepted here, so no component
// parses that string itself any more:
//   (a) single candidate   — one object:
//       { jobReferenceId, jobDescription, jobTitle, recruiterEmail,
//         email: "x@y.com", candidateName: "Name", candidateProfileId: 123,
//         candidateDetails: { summary, skils: [], resume_text: ["part", "part"] } }
//   (b) multiple candidates — an ARRAY of the objects in (a), one per candidate.
//   (c) legacy parallel arrays — one object whose `candidateName` / `email` /
//       `candidateProfileId` / `resumeText` are index-aligned arrays.
//
// `candidateDetails.resume_text` is a list of strings; the backend field is a
// single string, so the parts are joined (blank line between, nothing dropped
// or truncated).
const RESUME_PART_SEPARATOR = '\n\n';
const isObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const asString = (value) => value === null || value === undefined ? '' : String(value).trim();
const toStringArray = (value) => {
    if (Array.isArray(value))
        return value.map(asString);
    const single = asString(value);
    return single ? [single] : [];
};
// A resume_text value may be a string or a list of parts. Join, drop blanks.
const joinResumeParts = (value) => toStringArray(value)
    .filter((part) => part !== '')
    .join(RESUME_PART_SEPARATOR);
const pickDetails = (candidateDetails) => {
    var _a;
    if (!isObject(candidateDetails))
        return {};
    const details = {};
    const summary = asString(candidateDetails.summary);
    if (summary)
        details.summary = summary;
    const designation = asString(candidateDetails.designation);
    if (designation)
        details.designation = designation;
    // `skils` is the legacy misspelling the backend still accepts as an alias.
    const skills = toStringArray((_a = candidateDetails.skills) !== null && _a !== void 0 ? _a : candidateDetails.skils).filter(Boolean);
    if (skills.length)
        details.skills = skills;
    const education = toStringArray(candidateDetails.education).filter(Boolean);
    if (education.length)
        details.education = education;
    const experience = Number(candidateDetails.total_experience);
    if (Number.isFinite(experience) && candidateDetails.total_experience !== '') {
        details.total_experience = experience;
    }
    return details;
};
// Shape (c): the object itself carries index-aligned arrays.
const isParallelArrayShape = (item) => Array.isArray(item.email) ||
    Array.isArray(item.candidateName) ||
    Array.isArray(item.candidateProfileId);
const candidatesFromItem = (item) => {
    var _a, _b;
    if (isParallelArrayShape(item)) {
        const names = toStringArray(item.candidateName);
        const emails = toStringArray(item.email);
        const profileIds = toStringArray(item.candidateProfileId);
        // Per-candidate resumes are index-aligned here, never "parts of one resume".
        const resumes = toStringArray((_a = item.resumeText) !== null && _a !== void 0 ? _a : item.resume);
        const count = Math.max(names.length, emails.length, profileIds.length, 1);
        const details = pickDetails(item.candidateDetails);
        const detailsResume = count === 1
            ? joinResumeParts(isObject(item.candidateDetails) ? item.candidateDetails.resume_text : undefined)
            : '';
        return Array.from({ length: count }, (_, index) => ({
            name: names[index] || '',
            email: emails[index] || '',
            profileId: profileIds[index] || '',
            resumeText: resumes[index] || detailsResume,
            details: count === 1 ? details : {},
        }));
    }
    const candidateDetails = isObject(item.candidateDetails) ? item.candidateDetails : undefined;
    return [
        {
            name: asString(item.candidateName),
            email: asString(item.email),
            profileId: asString(item.candidateProfileId),
            // candidateDetails.resume_text wins; the old top-level keys stay supported.
            resumeText: joinResumeParts(candidateDetails === null || candidateDetails === void 0 ? void 0 : candidateDetails.resume_text) ||
                joinResumeParts((_b = item.resumeText) !== null && _b !== void 0 ? _b : item.resume),
            details: pickDetails(candidateDetails),
        },
    ];
};
const firstNonEmpty = (items, read) => {
    for (const item of items) {
        const value = read(item);
        if (value)
            return value;
    }
    return '';
};
export function parseScheduleSession(raw) {
    var _a;
    if (!raw)
        return null;
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch (_b) {
        return null;
    }
    const items = (Array.isArray(parsed) ? parsed : [parsed]).filter(isObject);
    if (!items.length)
        return null;
    const recruiterEmails = (_a = items.map((i) => toStringArray(i.recruiterEmail).filter(Boolean)).find((a) => a.length)) !== null && _a !== void 0 ? _a : [];
    return {
        job: {
            // Job-level fields come from the first element that has them, so it does
            // not matter whether Vriti repeats them on every candidate.
            jobReferenceId: firstNonEmpty(items, (i) => asString(i.jobReferenceId)),
            jobDescription: firstNonEmpty(items, (i) => asString(i.jobDescription)),
            jobTitle: firstNonEmpty(items, (i) => asString(i.jobTitle)),
            recruiterEmails,
        },
        candidates: items.flatMap(candidatesFromItem),
    };
}
